import axios from 'axios';
import {
  CreateSupportCaseRequest,
  SupportCase,
  SupportCaseStatus,
  UpdateSupportCaseStatusRequest,
} from './models/SupportCase';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';

const baseURL = process.env.REACT_APP_API_BASE_URL;

class SupportCasesApiClient {
  public axiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('access_token');
        const assumedUserId = localStorage.getItem('assumed_user_id');

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        if (assumedUserId) {
          config.headers['X-UserId'] = assumedUserId;
        }
        const assumedOrganizationId = localStorage.getItem('assumed_organization_id');
        if (assumedOrganizationId) {
          config.headers['X-OrganizationId'] = assumedOrganizationId;
        }

        applyClientChannelHeader(config.headers);
        applyAllowTestOrgsHeader(config.headers);

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response ? error.response.status : null;
        if (status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('phone_number');
        }
        return Promise.reject(error);
      }
    );
  }

  public async listSupportCases(): Promise<SupportCase[]> {
    const response = await this.axiosInstance.get<SupportCase[]>('/support-cases');
    return response.data;
  }

  public async getSupportCase(caseId: string): Promise<SupportCase> {
    const response = await this.axiosInstance.get<SupportCase>(`/support-cases/${caseId}`);
    return response.data;
  }

  public async createSupportCase(request: CreateSupportCaseRequest): Promise<SupportCase> {
    const response = await this.axiosInstance.post<SupportCase>('/support-cases', request);
    return response.data;
  }

  public async updateSupportCaseStatus(
    caseId: string,
    status: SupportCaseStatus
  ): Promise<SupportCase> {
    const body: UpdateSupportCaseStatusRequest = { status };
    const response = await this.axiosInstance.patch<SupportCase>(
      `/support-cases/${caseId}/status`,
      body
    );
    return response.data;
  }
}

export default SupportCasesApiClient;

export const supportCasesApiClient = new SupportCasesApiClient();
